/**
 * Map **detection-glyph** layer (REQ-UI-068; ADR-032 §4.1) — the Map side
 * of the Map <-> Detections synchronized-selection edge (REQ-UI-051 /
 * ADR-024 §5). Detection is the SAPIENT successor to the data-plane Event
 * surface, removed in the ADR-032 cascade (PR6).
 *
 * A non-visual React component (like :func:`SnapshotLayer`) that:
 *
 *   1. renders the shared detections store's rows as cyan glyphs, updating
 *      the MapLibre source in place (no re-attach churn — the cluster-
 *      flicker lesson). Since ADR-041 only a track's **head** gets a glyph
 *      (REQ-UI-169) — its earlier members are hidden and the object-track
 *      layer draws the line through them; the selection ring is unfiltered. A detection with a **null position** (unprojectable
 *      range/bearing, REQ-INGEST-044) is anchored at its reporting platform's
 *      marker — painted faint/approximate — when that platform is in the
 *      ``platformPositions`` index (REQ-UI-071); with no known platform
 *      position it stays list-only (Detections panel only, no glyph);
 *   2. originates selection: clicking a glyph writes
 *      ``{kind: "detection", id}`` to the bus;
 *   3. consumes selection: highlights the selected glyph's ring and pans
 *      the Map (200 ms ease) to centre the detection when it isn't already
 *      in the viewport AND has a non-null position;
 *   4. consumes the **force-recenter** signal (the 2026-06-09 two-slot
 *      redesign, Phase 2): when the Detections panel's "Select on Map" row
 *      menu raises :func:`requestMapFocus`, eases the camera to the
 *      detection **always** — even if already in view — distinct from the
 *      pan-if-not-in-view of a glyph click (a glyph click never raises the
 *      signal). REQ-UI-096.
 *
 * # This component only READS the detections store
 *
 * The shared detections store is kept warm by :func:`useMapSseBridges`
 * (mounted once in :file:`Map.tsx`), which mounts
 * :func:`useDetectionsSseBridge` against the Map's live ``sseMessage``, and
 * by the Detections panel, which mounts its own REST back-fill feed. This
 * layer therefore does **not** re-mount any feed or SSE bridge — it
 * subscribes to the store via :func:`useRecentDetections` and paints.
 * Re-mounting the SSE bridge here would double-process every delta.
 *
 * Mounted by :file:`Map.tsx` alongside the other data layers. Takes the live
 * ``map`` instance prop; it needs no ``sseMessage`` because it owns no
 * bridge.
 *
 * Display-only mode (REQ-UI-056) is unbuilt and not gated here —
 * consistent with the Map's platform-marker click; it will gate all
 * selection originators centrally when it lands.
 *
 * Cross-references:
 *   - REQ-UI-068 (Detection map glyphs) / REQ-UI-051 (Map <-> Detections) /
 *     REQ-UI-006 (live latency) / REQ-UI-002 (WebGL) /
 *     REQ-INGEST-044 (nullable position).
 *   - ADR-024 §5 (selection contract — 200 ms ease, pan-if-not-in-view);
 *     ADR-032 §4.1 (Detection supersedes Event).
 *   - map/use-map-sse-bridges.ts (the store-warming SSE bridge owner).
 *   - map/snapshot-layer.tsx (the lifecycle pattern mirrored here).
 */
import { useEffect, useMemo, useState } from "react";
import type { Map as MapLibreMap, MapLayerMouseEvent } from "maplibre-gl";
import { useRecentDetections, useDetectionsStore } from "@/detections/detections-store";
import { useMapSelectedIdForKind, useSelectionStore } from "@/selection/selection-store";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import { ageFadeTickMs } from "@/map/age-fade";
import { useLiveObjectTracks, useTrackIdForDetection } from "@/object-tracks/object-track-store";
import { useMapFocusSignal } from "@/selection/map-focus-store";
import {
  DETECTIONS_LAYER_ID,
  type PlatformPositionIndex,
  attachDetectionLayer,
  detachDetectionLayer,
  trackGlyphPlan,
  setSelectedDetectionHighlight,
} from "@/map/detection-layer-impl";
import { focusZoom } from "@/map/cluster-styling";

/**
 * Camera ease used when panning to a selected detection that isn't in the
 * viewport. Per ADR-024 §5 / REQ-UI-051 ("pans the Map with a 200 ms ease
 * to centre the … coordinates"). Same value as the event layer's pan.
 */
export const DETECTION_PAN_DURATION_MS = 200;

/** Props for :func:`DetectionLayer`. */
export interface DetectionLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
  /**
   * The active Profile's ``map.detection.single_color`` (ADR-033 §9; REQ-UI-087).
   * Colours the detection glyph; a blank/absent value falls back to the
   * ``--detection-glyph`` token. A colour change re-applies the glyph paint in
   * place (no teardown).
   */
  singleColor?: string;
  /**
   * The active Profile's ``map.detection.glyph`` shape (GLYPHS batch;
   * REQ-UI-087; the closed :data:`map/glyphs.GLYPH_IDS` vocabulary, default
   * ``circle``). Sets the SDF the glyph paints; an unknown id falls back to
   * ``circle``. A shape change re-applies the glyph's ``icon-image`` in place
   * (no teardown).
   */
  glyph?: string;
  /**
   * ``producer_id`` → ``[lng, lat]`` index of the current (viewport-scoped)
   * platform positions, from the snapshot (REQ-UI-071). A null-position
   * (unprojectable range/bearing) detection is anchored at its reporting
   * platform's marker — painted faint/approximate — when that platform is in
   * this index; otherwise it stays list-only. Omit to disable anchoring.
   */
  platformPositions?: PlatformPositionIndex;
}

/**
 * Render + wire the detection-glyph layer. Returns no DOM (WebGL-only).
 */
export function DetectionLayer({
  map,
  singleColor,
  glyph,
  platformPositions,
}: DetectionLayerProps): null {
  // Read-only: the store is fed by useMapSseBridges + the Detections panel's
  // REST feed (see module docstring). We subscribe + paint.
  const detections = useRecentDetections();
  // Ring + camera track the MAP selection slot (the two-slot redesign,
  // 2026-06-09): a Detections-list left-click inspects without moving the
  // ring; only a glyph click (selectOnMap, below) sets it.
  const selectedReportId = useMapSelectedIdForKind("detection");
  // A glyph click is a map-originated selection → set BOTH slots so the
  // ring/pan AND Entity Details move together.
  const selectOnMap = useSelectionStore((s) => s.selectOnMap);
  // The list-only "Select on Map" force-recenter signal (the 2026-06-09
  // two-slot redesign, Phase 2). A glyph click never raises this; only the
  // Detections-panel row menu does → so a glyph click can't force-recenter.
  const focusSignal = useMapFocusSignal();

  // Lifecycle: detach the source + layers ONCE on unmount (or a rare ``map``
  // change). Decoupled from data updates so glyph arrivals / expirations
  // don't tear down + re-create the source.
  useEffect(() => {
    return () => {
      detachDetectionLayer(map);
    };
  }, [map]);

  // Data-update: idempotent attach. First call adds the source + glyph layer
  // + highlight layer (even when empty, so the click handler + ring are
  // ready); later calls ``setData`` against the live source. Keyed on
  // ``singleColor`` + ``glyph`` too (REQ-UI-087) so a Profile colour/shape
  // change re-runs the attach, whose existing-source path re-applies the glyph
  // icon + colour in place.
  //
  // ADR-041 D-4 / REQ-UI-168 / -169: one glyph per track — the head — and the
  // **object-track store** is the authority for which member that is, as
  // REQ-UI-168's Notes always said. Deriving it from the detections buffer
  // instead meant a track quiet for longer than that buffer's 500-row horizon
  // rendered a line with no head at all, and one whose true head had been
  // evicted rendered a glyph at a stale position (probed 2026-10-09; see
  // :func:`trackGlyphPlan`). The plan also carries the selected member when
  // the buffer has lost it, so the ring still draws at any selected
  // detection.
  const tracks = useLiveObjectTracks();
  const liveWindowMs = useLiveWindowMs();
  const selectedTrackId = useTrackIdForDetection(selectedReportId);
  const selectedMember = useMemo(
    () =>
      selectedReportId !== null && selectedTrackId !== null
        ? { reportId: selectedReportId, trackId: selectedTrackId }
        : null,
    [selectedReportId, selectedTrackId],
  );
  const plan = useMemo(
    () => trackGlyphPlan(detections, tracks, selectedMember),
    [detections, tracks, selectedMember],
  );
  // ``selectedReportId`` is a dependency because the heads-only source keeps
  // the selected detection even when it is a non-head member (REQ-UI-169): a
  // selection change may add or drop one feature.
  // The age fade (REQ-UI-167's "the same schedule as detection glyphs", which
  // the glyphs did not previously honour): re-convert on a slow tick as well
  // as on every store change, so a detection that has stopped being re-sent
  // keeps fading instead of sitting bright until it is pruned. The interval
  // comes from the deployment's live window — 15 s at the 10-minute default,
  // finer for a shorter one (:func:`age-fade.ageFadeTickMs`).
  const [ageTick, setAgeTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setAgeTick((t) => t + 1), ageFadeTickMs(liveWindowMs));
    return () => {
      window.clearInterval(id);
    };
  }, [liveWindowMs]);

  useEffect(() => {
    attachDetectionLayer(
      map,
      detections,
      singleColor,
      glyph,
      platformPositions,
      plan,
      selectedReportId,
      { now: Date.now(), liveWindowMs },
    );
    // ``ageTick`` is a deliberate dependency: it re-runs the conversion so the
    // age band advances with no store change.
  }, [
    map,
    detections,
    singleColor,
    glyph,
    platformPositions,
    plan,
    selectedReportId,
    liveWindowMs,
    ageTick,
  ]);

  // Originate selection: a glyph click writes detection-kind selection to
  // BOTH slots (map-originated → selectOnMap; rings + Entity Details together).
  useEffect(() => {
    const onClick = (event: MapLayerMouseEvent): void => {
      const id = event.features?.[0]?.properties?.report_id;
      if (typeof id === "string" && id.length > 0) {
        selectOnMap({ kind: "detection", id });
      }
    };
    map.on("click", DETECTIONS_LAYER_ID, onClick);
    return () => {
      map.off("click", DETECTIONS_LAYER_ID, onClick);
    };
  }, [map, selectOnMap]);

  // Consume selection — highlight ring. Re-applied on ``detections`` change
  // too, so the filter survives a source rebuild / first attach.
  useEffect(() => {
    setSelectedDetectionHighlight(map, selectedReportId);
  }, [map, selectedReportId, detections]);

  // Consume selection — pan. Fires on selection *change* only (not on every
  // detections update), so a live detection arriving while a selection is
  // active doesn't re-pan. Coordinates are read from the store snapshot at
  // fire time. Pans only when the detection isn't already in the viewport
  // AND has a non-null position (REQ-INGEST-044 — a null-position detection
  // has no map point to pan to; it's list-only). A cold selection
  // (detection not buffered — e.g., a shareable-URL select) has no
  // coordinates here and is left to Entity Details.
  useEffect(() => {
    if (selectedReportId === null) {
      return;
    }
    const detection = useDetectionsStore.getState().byId[selectedReportId];
    if (detection === undefined) {
      return;
    }
    if (detection.longitude_deg === null || detection.latitude_deg === null) {
      return;
    }
    const center: [number, number] = [detection.longitude_deg, detection.latitude_deg];
    if (map.getBounds().contains(center)) {
      return;
    }
    map.easeTo({ center, duration: DETECTION_PAN_DURATION_MS });
  }, [map, selectedReportId]);

  // Consume the force-recenter signal — the list "Select on Map" path
  // (REQ-UI-096; the 2026-06-09 two-slot redesign). Fires on the focus
  // ``nonce`` change so a repeat "Select on Map" on the same row recenters
  // again. Unlike the pan effect above, this eases to the detection
  // **regardless** of the in-viewport check (the gesture's whole point is to
  // make an off-screen glyph the camera focus). Gated to this layer's kind +
  // the current map selection so an alert / platform focus request is ignored
  // here. The null-position gate (REQ-INGEST-044) still applies — an
  // unlocated detection has no point to recenter to (and the panel disables
  // the menu item for it anyway).
  useEffect(() => {
    if (focusSignal.nonce === 0) {
      return; // No focus requested yet.
    }
    const target = focusSignal.target;
    if (target === null || target.kind !== "detection") {
      return; // Not a detection focus request.
    }
    if (target.id !== selectedReportId) {
      return; // selectOnMap sets mapSelected in the same gesture; stay in sync.
    }
    const detection = useDetectionsStore.getState().byId[target.id];
    if (detection === undefined) {
      return; // Cold selection — coords unknown here; Entity Details handles it.
    }
    if (detection.longitude_deg === null || detection.latitude_deg === null) {
      return; // List-only (REQ-INGEST-044) — nothing to recenter to.
    }
    // Zoom IN to un-cluster (REQ-UI-096): at cluster-level zoom the glyph is
    // hidden inside a bubble, so an explicit "Select on Map" eases to
    // ``focusZoom`` (≥ CLUSTER_MAX_ZOOM + 1) where it paints individually for
    // the ring to land on. An already-zoomed-in map keeps its zoom (the
    // operator's detail level is preserved; never zoomed out). The passive
    // pan-if-not-in-view effect above deliberately does NOT change zoom.
    map.easeTo({
      center: [detection.longitude_deg, detection.latitude_deg],
      zoom: focusZoom(map.getZoom()),
      duration: DETECTION_PAN_DURATION_MS,
    });
    // Keyed on ``nonce`` (the changing signal); ``selectedReportId`` and the
    // ``target`` are read at fire time. ``focusSignal.target`` is intentionally
    // not a dep — only a fresh request (nonce bump) should recenter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusSignal.nonce]);

  return null;
}
